import React, { useMemo } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { seriesStop, tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import LegendTable, { ILegendTableRow, LegendTableDensity } from '../LegendTable';
import SegmentedDistributionBar, {
  IDistributionSegment,
  SegmentedDistributionBarHeight
} from '../SegmentedDistributionBar';

export type ReadinessBandKey =
  | 'ready'
  | 'potential'
  | 'notReady'
  | 'suppressed'
  | 'insufficientData';

export interface IReadinessBand {
  band: ReadinessBandKey;
  /** Share of the eligible base as a 0–1 fraction, the family's share scale. */
  pct: number;
  /** Pre-formatted customer count, so the legend reads exactly as the card's own figures. */
  value: string;
}

export interface IReadinessDistributionProps {
  bands: IReadinessBand[];
  height?: SegmentedDistributionBarHeight;
  density?: LegendTableDensity;
  className?: string;
}

interface BandPresentation {
  labelKey: string;
  color: string;
}

/**
 * Readiness is ordinal — a customer moves down this list as the propensity signal weakens — so the
 * bar and legend are painted in this order whatever order the payload arrives in, and a band the
 * payload omits simply does not appear.
 */
const BAND_ORDER: ReadinessBandKey[] = [
  'ready',
  'potential',
  'notReady',
  'suppressed',
  'insufficientData'
];

/**
 * PERX3-REDESIGN round 5: the bands take the tonal ramp in `BAND_ORDER`, so the strongest band is
 * the darkest and the bar reads as one scale instead of four unrelated hues. `insufficientData`
 * names no position on that scale, so it takes the ramp's neutral stop rather than the next tone
 * down. Each stop falls back to the hue the band already drew, which is the flag-OFF render.
 */
const BAND_PRESENTATION: Record<ReadinessBandKey, BandPresentation> = {
  ready: { labelKey: 'Ready', color: seriesStop(1, tokens.color.positive) },
  potential: { labelKey: 'Potential', color: seriesStop(2, tokens.color['accent-cyan']) },
  notReady: { labelKey: 'NotReady', color: seriesStop(3, tokens.color.warning) },
  suppressed: { labelKey: 'Suppressed', color: seriesStop(4, tokens.color['accent-purple']) },
  insufficientData: {
    labelKey: 'InsufficientData',
    color: seriesStop(8, tokens.color['text-muted'])
  }
};

const Host = styled.div`
  display: flex;
  flex-direction: column;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['12']};
  }
`;

const Disclaimer = styled.p`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const ReadinessDistribution: React.FC<IReadinessDistributionProps> = ({
  bands,
  height,
  density,
  className
}) => {
  const { t } = useMultiLanguageTranslation();

  // A payload that repeats a band would otherwise paint it twice and hand LegendTable a duplicate
  // row key; the last reading for a band wins.
  const bandsByKey = useMemo(
    () => new Map((Array.isArray(bands) ? bands : []).map(band => [band.band, band])),
    [bands]
  );

  const orderedBands = useMemo(
    () => BAND_ORDER.filter(key => bandsByKey.has(key)).map(key => bandsByKey.get(key)),
    [bandsByKey]
  );

  const segments: IDistributionSegment[] = orderedBands.map(band => ({
    label: t(
      `${TCustomerInsights}.ProductReadiness.Bands.${BAND_PRESENTATION[band.band].labelKey}`
    ),
    pct: band.pct,
    color: BAND_PRESENTATION[band.band].color
  }));

  const legendRows: ILegendTableRow[] = orderedBands.map((band, index) => ({
    color: segments[index].color,
    label: segments[index].label,
    value: band.value,
    pct: band.pct
  }));

  if (orderedBands.length === 0) {
    return null;
  }

  return (
    <Host className={className} data-testid="readiness-distribution">
      <SegmentedDistributionBar segments={segments} showInlineLabels={false} height={height} />
      <LegendTable rows={legendRows} density={density} />
      <Disclaimer data-testid="readiness-distribution-disclaimer">
        {t(`${TCustomerInsights}.ProductReadiness.Disclaimer`)}
      </Disclaimer>
    </Host>
  );
};

export default ReadinessDistribution;
